// @ts-ignore
import { RegisteredFileStrings } from "./RegisteredFileStrings";
import { getIntlConfigByLocale } from "../util";

export interface IntlProviderOptions {
  apiService: {
    basePath: `./static/locale`
  }
  // debug: boolean
  // performance: {}
}

export type DecodedJsonData = { [key:string]: any }

export type SupportedLocales = ReadonlySet<Intl.UnicodeBCP47LocaleIdentifier>;
export type LocaleID = Intl.UnicodeBCP47LocaleIdentifier;
export type FallbackLocale = LocaleID;
export type RollupLocales = Map<string, string[]>;
export type UIStrings = Record<string, string>;
export type LocalizedMessages = Record<string, {message: string}>;

export class I18n {
  readonly supportedLocales: ReadonlySet<Intl.UnicodeBCP47LocaleIdentifier>;

  readonly fallbackLocale:FallbackLocale;

  private localeData = new Map<Intl.UnicodeBCP47LocaleIdentifier, LocalizedMessages>();

  constructor(supportedLocales: SupportedLocales, fallbackLocale: FallbackLocale) {
    this.supportedLocales = new Set(supportedLocales);
    this.fallbackLocale = fallbackLocale;
  }

  registerLocaleData(locale: Intl.UnicodeBCP47LocaleIdentifier, messages: LocalizedMessages): void {
    this.localeData.set(locale, messages);
  }

  registerFileStrings(filename: string, stringStructure: UIStrings): RegisteredFileStrings {
    return new RegisteredFileStrings(filename, stringStructure, this.localeData);
  }

  async loadLocaleFile(locale: Intl.UnicodeBCP47LocaleIdentifier, options: Pick<IntlProviderOptions, 'apiService'>) {
    return getIntlConfigByLocale( locale, options );
  }

  /**
   * Attempts to canonicalize the given locale;
   * Uses fallbackLocale on error in canonicalization
   */
  getCanonicalLocaleOrFallback(locale: Intl.UnicodeBCP47LocaleIdentifier): Intl.UnicodeBCP47LocaleIdentifier {
    try {
      // @ts-expect-error https://github.com/microsoft/TypeScript/issues/29129
      return Intl.getCanonicalLocales(locale)[0];
    } catch(e) {
      // @ts-ignore
      console.log(`I18n :: Provided locale ${locale} is invalid. Using fallback ${this.fallbackLocale}.`);
      return this.fallbackLocale;
    }
  }

  /**
   * Look up the best available locale for the requested language through these fall backs:
   * - exact match
   * - progressively shorter prefixes (`de-CH-1996` -> `de-CH` -> `de`)
   * - the default locale if no match is found
   */
  lookupClosestSupportedLocale(locale: Intl.UnicodeBCP47LocaleIdentifier): Intl.UnicodeBCP47LocaleIdentifier {
    const canonicalLocale: Intl.UnicodeBCP47LocaleIdentifier = this.getCanonicalLocaleOrFallback(locale);
    const localeParts = canonicalLocale.split('-');
    while (localeParts.length) {
      const candidate = localeParts.join('-');
      if (this.supportedLocales.has(candidate)) {
        return candidate;
      }
      localeParts.pop();
    }
    return this.fallbackLocale;
  }

  determineLocale(): Intl.UnicodeBCP47LocaleIdentifier {
    const strategies = [
      { name: 'searchParams', test: () => new URLSearchParams(window.location.search).get('locale') },
      { name: 'navigatorLanguage', test: () => navigator.language },
      { name: 'htmlLangTag', test: () => document?.querySelector('html')?.getAttribute('lang') },
      { name: 'fallback', test: () => this.fallbackLocale },
    ];

    let foundLocale:string = '';

    const test = strategies.some((strategy) => {
      const res = strategy.test();
      if(res && this.supportedLocales.has(res)) {
        foundLocale = res;
      }
      else if (res && strategy.name !== 'fallback') {
        const match = this.lookupClosestSupportedLocale(res);
        if(match && match !== this.fallbackLocale) {
          foundLocale = match;
        }
      }
      if(foundLocale) {
        // @ts-ignore
        console.log(`Found locale ${res} using strategy ${strategy.name}`);
      }

      return foundLocale;
    });

    return foundLocale;
  }
}
